import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import type { RootState } from '@/redux'
import type {
  FlowStateName,
  ModalContentState,
  HarvestCamp,
  ProposedPanelV1,
  CredentialOption,
  CredentialAvailabilitySummary,
} from '@/types/DashboardV1.types'
import type { ThinPreviewWarning } from '@/types/Dashboards.types'
import type {
  DiscoveredFilterValue,
  FilterDiscoveryStatus,
} from '@/types/DashboardFilterDiscovery.types'

// ─── State shape (§3d.7) ─────────────────────────────────────────────────────

export interface DashboardCreationState {
  sessionId: string | null
  dashboardId: string | null
  flowState: FlowStateName
  dashboardName: string
  /** Whether the user has manually typed a name (prevents AI auto-rename). */
  nameManuallyEdited: boolean
  sourceAppCode: string | null
  credentialId: string | null
  /**
   * Existing credentials returned by the `present_credential` tool-call when
   * the FSM enters awaiting_credential (TDD §3d.1.E).
   *
   * An empty array is a VALID signal — it means the org has no existing active
   * credentials for the selected source app, so the frontend renders the Harvest
   * CTA (mockup 04). A non-empty array triggers the radiogroup picker (mockup 05).
   *
   * Populated by the `present_credential` tool-call handler in DashboardChatService.
   * Reset on slice `reset`.
   */
  availableCredentials: CredentialOption[]
  selectedDomainIds: string[]
  /**
   * Filter values stored by namespaced key `<domainId>.<filterCode>`.
   * Values are either a single string or a string array (multi-select).
   */
  domainFilterValues: Record<string, string | string[]>
  /**
   * Discovered filter choices keyed by `<domainId>.<filterCode>` (Stage 2B —
   * TDD §Frontend Side). null = not yet fetched. [] = empty discovery result
   * (drives the blocking-zero-seasons UI for the `season` filter, mockup
   * `2026-04-28-filter-enrollment-zero-seasons-v1.html`). Loading / error are
   * tracked in the parallel `domainFilterStatus` map.
   */
  domainFilterChoices: Record<string, DiscoveredFilterValue[] | null>
  /**
   * Per-`<domainId>.<filterCode>` discovery status. See `FilterDiscoveryStatus`
   * for the full state machine. Mockups:
   *   - loading → 2026-04-28-filter-enrollment-loading-v1.html
   *   - error → 2026-04-28-filter-enrollment-error-v1.html
   *   - credential_expired → 2026-04-28-filter-enrollment-error-v1.html (re-harvest CTA)
   *   - rate_limited → countdown UX (Bug #3 2026-05-14); pairs with
   *     domainFilterRateLimitRetryAfter for the retry-after seconds.
   */
  domainFilterStatus: Record<string, FilterDiscoveryStatus>
  /**
   * Per-`<domainId>.<filterCode>` retry-after window in seconds when the
   * backend returned 503 RATE_LIMITED. Pairs with domainFilterStatus
   * `'rate_limited'`. Undefined entries imply no rate-limit state.
   * Bug #3 (2026-05-14).
   */
  domainFilterRateLimitRetryAfter: Record<string, number>
  /**
   * Loading / error status for the proposed-panels endpoint call.
   * Dispatched by `proposePanelsThunk` in DashboardProposedPanelsService.
   *   'idle'    — not yet called.
   *   'loading' — request in flight (PanelsProposedBlock shows skeleton).
   *   'ready'   — response received, proposedPanels is populated.
   *   'fsm_conflict' — 409 from backend (Confirm hit out of FSM order).
   *   'error'   — other 4xx / 5xx (retry CTA shown).
   */
  proposedPanelsStatus: 'idle' | 'loading' | 'ready' | 'fsm_conflict' | 'error'
  proposedPanels: ProposedPanelV1[]
  confirmedPanelIds: string[]
  /**
   * Build status — tracks the POST /v1/dashboards/:id/build call.
   *   'idle'               — not yet called (or reset after retry).
   *   'loading'            — request in flight (Build button shows spinner).
   *   'complete'           — build succeeded; socket drives final navigation.
   *   'unsupported_domain' — 400 UNSUPPORTED_DOMAIN (non-enrollment domain).
   *   'missing_credential' — 400 MISSING_CREDENTIAL.
   *   'unknown_panel'      — 400 UNKNOWN_PANEL_ID (should not happen in normal flow).
   *   'fsm_conflict'       — 409 INVALID_FSM_TRANSITION (already complete → navigate to canvas).
   *   'error'              — 5xx or other failure (generic retry CTA).
   * Dispatched by `buildDashboardThunk` in DashboardViewService.
   */
  buildStatus: 'idle' | 'loading' | 'complete' | 'unsupported_domain' | 'missing_credential' | 'unknown_panel' | 'fsm_conflict' | 'error'
  /** domainId from a 400 UNSUPPORTED_DOMAIN error — used to show a targeted message. */
  buildErrorDomainId: string | null
  /**
   * actualState from a 409 INVALID_FSM_TRANSITION error — the flow_state the BE
   * *actually* sees on the session. Drives step-specific copy in BuildErrorWell
   * so the FE no longer unconditionally claims "Dashboard already built" for
   * every 409 (Bug #4, 2026-05-14 bugbash). Null when not in an fsm_conflict
   * state, OR when the BE did not include actualState (defensive — falls back
   * to the existing "already built" copy in that case).
   *
   * String (not narrowed) so future BE additions don't require an FE union
   * extension. Practical values: 'awaiting_source' | 'awaiting_credential' |
   * 'awaiting_domain' | 'awaiting_filters' | 'syncing' | 'complete' or
   * `edit:<state>` for edit-mode sessions.
   */
  buildErrorActualState: string | null
  framing: string | null

  // ── Harvest modal state ───────────────────────────────────────────────────
  harvestSessionId: string | null
  harvestLiveViewUrl: string | null
  harvestContentState: ModalContentState
  /**
   * Camp list scraped from CampMinder camp-switcher at login_complete,
   * BEFORE the API key is created. Populated from harvest:progress
   * `picking_camp` event payload. Empty array for single-camp users.
   */
  harvestCamps: HarvestCamp[]
  /** The camp the user selected pre-harvest. Set on selectClientIdThunk fulfilled. */
  harvestSelectedClientIdPreharvest: string | null
  harvestFailure: { code: string; partialSuccess: boolean } | null
  /** ISO-8601 expiry date for the created API key. Populated on harvest:progress `complete`. */
  harvestKeyExpiresAt: string | null
  /** CampMinder camp name for the selected camp. Populated on harvest:progress `complete`. */
  harvestCampName: string | null
  /**
   * Thin-preview warning — set when the backend's propose_panels response includes a
   * thin-data flag (totalRowCount < 20 across all domains). Null when no warning.
   * Cleared when the user acts on a suggestion chip or when a new session starts.
   * §3d.5 / brand-guide §24
   */
  thinPreviewWarning: ThinPreviewWarning | null
  /**
   * Last error message from `prefetchAvailableCredentialsForApp` — populated when
   * the deterministic credential prefetch fails (network error, 4xx/5xx, or
   * unexpected throw). Null when prefetch succeeded or has not run.
   *
   * Before this field existed, prefetch failures silently dispatched
   * `setAvailableCredentials([])` — indistinguishable from "no credentials exist"
   * — and dead-ended users with existing creds into the harvest path. The harvest
   * block reads this field to surface a visible error state and offer a retry
   * affordance.
   *
   * Cleared on `reset` and when prefetch re-fires successfully.
   */
  credentialPrefetchError: string | null
}

const initialState: DashboardCreationState = {
  sessionId: null,
  dashboardId: null,
  flowState: 'awaiting_source',
  dashboardName: 'Untitled Dashboard',
  nameManuallyEdited: false,
  sourceAppCode: null,
  credentialId: null,
  availableCredentials: [],
  selectedDomainIds: [],
  domainFilterValues: {},
  domainFilterChoices: {},
  domainFilterStatus: {},
  domainFilterRateLimitRetryAfter: {},
  proposedPanelsStatus: 'idle',
  proposedPanels: [],
  confirmedPanelIds: [],
  buildStatus: 'idle',
  buildErrorDomainId: null,
  buildErrorActualState: null,
  framing: null,
  harvestSessionId: null,
  harvestLiveViewUrl: null,
  harvestContentState: 'launching',
  harvestCamps: [],
  harvestSelectedClientIdPreharvest: null,
  harvestFailure: null,
  harvestKeyExpiresAt: null,
  harvestCampName: null,
  thinPreviewWarning: null,
  credentialPrefetchError: null,
}

// ─── Slice ────────────────────────────────────────────────────────────────────

const dashboardCreationSlice = createSlice({
  name: 'dashboardCreation',
  initialState,
  reducers: {
    startSession(
      state,
      action: PayloadAction<{ sessionId: string; dashboardId: string; dashboardName?: string }>
    ) {
      state.sessionId = action.payload.sessionId
      state.dashboardId = action.payload.dashboardId
      if (action.payload.dashboardName) {
        state.dashboardName = action.payload.dashboardName
      }
    },

    setFlowState(state, action: PayloadAction<FlowStateName>) {
      state.flowState = action.payload
    },

    setSourceAppCode(state, action: PayloadAction<string | null>) {
      state.sourceAppCode = action.payload
    },

    setCredentialId(state, action: PayloadAction<string | null>) {
      state.credentialId = action.payload
    },

    /**
     * setAvailableCredentials — populated by the `present_credential` tool-call
     * handler when the FSM enters awaiting_credential (TDD §3d.1.E).
     *
     * An empty array is a valid signal (no existing creds → render Harvest CTA).
     * A non-empty array triggers the radiogroup credential picker.
     */
    setAvailableCredentials(state, action: PayloadAction<CredentialOption[]>) {
      state.availableCredentials = action.payload
    },

    /**
     * setCredentialPrefetchError — populated when
     * `prefetchAvailableCredentialsForApp` fails. Pass null to clear (e.g. when
     * a retry succeeds). See `credentialPrefetchError` field comment for the
     * silent-failure regression this fixes.
     */
    setCredentialPrefetchError(state, action: PayloadAction<string | null>) {
      state.credentialPrefetchError = action.payload
    },

    /**
     * setAvailabilityForCredential — patches a single credential's
     * availabilitySummary in the availableCredentials array without triggering
     * a full refetch. Dispatched by `recheckCredentialAvailability` after a
     * successful POST /v1/credentials/:credentialId/capability/probe.
     *
     * If blockedReason is absent on the returned summary, the picker row
     * re-renders as a selectable radiogroup pill in-place.
     * Phase E Slice 2.4 — TDD §5.2.
     */
    setAvailabilityForCredential(
      state,
      action: PayloadAction<{ credentialId: string; availabilitySummary: CredentialAvailabilitySummary }>
    ) {
      const { credentialId, availabilitySummary } = action.payload
      const idx = state.availableCredentials.findIndex(c => c.id === credentialId)
      if (idx >= 0) {
        state.availableCredentials[idx] = {
          ...state.availableCredentials[idx],
          availabilitySummary,
        }
      }
    },

    toggleDomainId(state, action: PayloadAction<string>) {
      const id = action.payload
      const idx = state.selectedDomainIds.indexOf(id)
      if (idx >= 0) {
        state.selectedDomainIds = state.selectedDomainIds.filter(d => d !== id)
      } else {
        state.selectedDomainIds = [...state.selectedDomainIds, id]
      }
    },

    setSelectedDomainIds(state, action: PayloadAction<string[]>) {
      state.selectedDomainIds = action.payload
    },

    setDomainFilterValue(
      state,
      action: PayloadAction<{ key: string; value: string | string[] }>
    ) {
      state.domainFilterValues[action.payload.key] = action.payload.value
    },

    /**
     * setFilterChoices — populated by `discoverFilterValues` thunk on a
     * successful discovery response (TDD §Frontend Side §1).
     *
     * Empty array is a valid signal: the `season` filter uses it to drive the
     * blocking-zero-seasons UI; downstream filters use it to mean "no parent
     * selected yet" or "no children for the selected parents".
     */
    setFilterChoices(
      state,
      action: PayloadAction<{
        domainId: string
        filterCode: string
        values: DiscoveredFilterValue[]
      }>
    ) {
      const key = `${action.payload.domainId}.${action.payload.filterCode}`
      state.domainFilterChoices[key] = action.payload.values
    },

    /**
     * setFilterStatus — drives the FiltersBlock state machine
     * (idle | loading | ready | error | credential_expired | rate_limited).
     * The discovery thunk dispatches `loading` first, then one of the
     * terminal states based on the response. Bug #3 (2026-05-14) added
     * `rate_limited`; the retry-after seconds live in a sibling map via
     * setFilterRateLimit.
     */
    setFilterStatus(
      state,
      action: PayloadAction<{
        domainId: string
        filterCode: string
        status: FilterDiscoveryStatus
      }>
    ) {
      const key = `${action.payload.domainId}.${action.payload.filterCode}`
      state.domainFilterStatus[key] = action.payload.status
    },

    /**
     * setFilterRateLimit — stores (or clears) the retry-after window for a
     * `<domainId>.<filterCode>` key. Pairs with `setFilterStatus('rate_limited')`.
     * Passing null clears the entry (e.g. when a retry succeeds and the
     * status transitions back to `ready` or `loading`). Bug #3 (2026-05-14).
     */
    setFilterRateLimit(
      state,
      action: PayloadAction<{
        domainId: string
        filterCode: string
        retryAfterSeconds: number | null
      }>
    ) {
      const key = `${action.payload.domainId}.${action.payload.filterCode}`
      if (action.payload.retryAfterSeconds === null) {
        delete state.domainFilterRateLimitRetryAfter[key]
      } else {
        state.domainFilterRateLimitRetryAfter[key] =
          action.payload.retryAfterSeconds
      }
    },

    /**
     * clearFiltersForDomain — drops all discovery state and selected values
     * for a domain. Called when the user navigates away from a domain (e.g.
     * deselects it at Step 3) so re-entering refetches fresh data.
     *
     * Matches both the namespaced-key prefix `<domainId>.` against
     * domainFilterValues, domainFilterChoices, and domainFilterStatus.
     */
    clearFiltersForDomain(state, action: PayloadAction<string>) {
      const prefix = `${action.payload}.`
      for (const key of Object.keys(state.domainFilterValues)) {
        if (key.startsWith(prefix)) delete state.domainFilterValues[key]
      }
      for (const key of Object.keys(state.domainFilterChoices)) {
        if (key.startsWith(prefix)) delete state.domainFilterChoices[key]
      }
      for (const key of Object.keys(state.domainFilterStatus)) {
        if (key.startsWith(prefix)) delete state.domainFilterStatus[key]
      }
      for (const key of Object.keys(state.domainFilterRateLimitRetryAfter)) {
        if (key.startsWith(prefix))
          delete state.domainFilterRateLimitRetryAfter[key]
      }
    },

    /**
     * setProposedPanelsStatus — drives the PanelsProposedBlock state machine.
     * Dispatched by `proposePanelsThunk` in DashboardProposedPanelsService.
     */
    setProposedPanelsStatus(
      state,
      action: PayloadAction<'idle' | 'loading' | 'ready' | 'fsm_conflict' | 'error'>
    ) {
      state.proposedPanelsStatus = action.payload
    },

    /**
     * setBuildStatus — drives the PanelsProposedBlock build-CTA state machine.
     * Dispatched by `buildDashboardThunk` in DashboardViewService.
     */
    setBuildStatus(
      state,
      action: PayloadAction<DashboardCreationState['buildStatus']>
    ) {
      state.buildStatus = action.payload
    },

    /**
     * setBuildErrorDomainId — stores the domainId from a 400 UNSUPPORTED_DOMAIN
     * error so PanelsProposedBlock can render a targeted "not yet supported" message.
     */
    setBuildErrorDomainId(state, action: PayloadAction<string | null>) {
      state.buildErrorDomainId = action.payload
    },

    /**
     * setBuildErrorActualState — stores the actual flow_state the BE reported
     * in a 409 INVALID_FSM_TRANSITION error so PanelsProposedBlock's
     * BuildErrorWell can render step-specific copy (Bug #4, 2026-05-14).
     */
    setBuildErrorActualState(state, action: PayloadAction<string | null>) {
      state.buildErrorActualState = action.payload
    },

    setProposedPanels(state, action: PayloadAction<ProposedPanelV1[]>) {
      state.proposedPanels = action.payload
      state.confirmedPanelIds = action.payload
        .filter(p => p.defaultInFirstDashboard)
        .map(p => p.panelId)
    },

    toggleConfirmedPanel(state, action: PayloadAction<string>) {
      const id = action.payload
      const idx = state.confirmedPanelIds.indexOf(id)
      if (idx >= 0) {
        state.confirmedPanelIds = state.confirmedPanelIds.filter(p => p !== id)
      } else {
        state.confirmedPanelIds = [...state.confirmedPanelIds, id]
      }
    },

    setFraming(state, action: PayloadAction<string | null>) {
      state.framing = action.payload
    },

    renameDashboard(state, action: PayloadAction<{ name: string; manual: boolean }>) {
      state.dashboardName =
        action.payload.name.trim() !== '' ? action.payload.name : 'Untitled Dashboard'
      state.nameManuallyEdited = action.payload.manual
    },

    // ── Harvest actions ────────────────────────────────────────────────────

    setHarvestSessionId(state, action: PayloadAction<string | null>) {
      state.harvestSessionId = action.payload
    },

    setHarvestLiveViewUrl(state, action: PayloadAction<string | null>) {
      state.harvestLiveViewUrl = action.payload
    },

    setHarvestContentState(state, action: PayloadAction<ModalContentState>) {
      state.harvestContentState = action.payload
    },

    setHarvestCamps(state, action: PayloadAction<HarvestCamp[]>) {
      state.harvestCamps = action.payload
    },

    setHarvestSelectedClientIdPreharvest(state, action: PayloadAction<string | null>) {
      state.harvestSelectedClientIdPreharvest = action.payload
    },

    setHarvestFailure(
      state,
      action: PayloadAction<{ code: string; partialSuccess: boolean } | null>
    ) {
      state.harvestFailure = action.payload
    },

    setHarvestKeyExpiresAt(state, action: PayloadAction<string | null>) {
      state.harvestKeyExpiresAt = action.payload
    },

    setHarvestCampName(state, action: PayloadAction<string | null>) {
      state.harvestCampName = action.payload
    },

    clearHarvest(state) {
      state.harvestSessionId = null
      state.harvestLiveViewUrl = null
      state.harvestContentState = 'launching'
      state.harvestCamps = []
      state.harvestSelectedClientIdPreharvest = null
      state.harvestFailure = null
      state.harvestKeyExpiresAt = null
      state.harvestCampName = null
    },

    // ── Thin-preview warning actions (§3d.5 / brand-guide §24) ───────────────

    setThinPreviewWarning(
      state,
      action: PayloadAction<ThinPreviewWarning>
    ) {
      state.thinPreviewWarning = action.payload
    },

    clearThinPreviewWarning(state) {
      state.thinPreviewWarning = null
    },

    /**
     * setDashboardId — seeds dashboardId (and optionally dashboardName) into the creation slice
     * without implying a session has been started. Use this wherever DashboardNew previously
     * dispatched startSession with an empty sessionId purely to satisfy navigation guards.
     *
     * The real startSession (with a valid sessionId) fires later from loadChatSession once the
     * backend AI session is established.
     */
    setDashboardId(
      state,
      action: PayloadAction<{ dashboardId: string; dashboardName?: string }>
    ) {
      state.dashboardId = action.payload.dashboardId
      if (action.payload.dashboardName) {
        state.dashboardName = action.payload.dashboardName
      }
    },

    reset() {
      return initialState
    },
  },
})

export const dashboardCreationReducer = dashboardCreationSlice.reducer
export const dashboardCreationActions = dashboardCreationSlice.actions
export default dashboardCreationSlice

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectCurrentFlowState = (state: RootState): FlowStateName =>
  state.dashboardCreation.flowState

export const selectSelectedDomainIds = (state: RootState): string[] =>
  state.dashboardCreation.selectedDomainIds

/**
 * selectFilterChoices — discovered choices for a `<domainId>.<filterCode>`
 * key (Stage 2B, TDD §Frontend Side). Returns `undefined` when never fetched,
 * `[]` when discovery returned empty (e.g. zero-seasons blocking trigger),
 * or a non-empty array on success.
 */
export const selectFilterChoices =
  (domainId: string, filterCode: string) =>
  (state: RootState): DiscoveredFilterValue[] | null | undefined =>
    state.dashboardCreation.domainFilterChoices[`${domainId}.${filterCode}`]

/**
 * selectFilterStatus — current discovery status for a `<domainId>.<filterCode>`
 * key. Returns `'idle'` for keys never seen by the thunk so callers can
 * branch directly on the union without a defensive null-check.
 */
export const selectFilterStatus =
  (domainId: string, filterCode: string) =>
  (state: RootState): FilterDiscoveryStatus =>
    state.dashboardCreation.domainFilterStatus[`${domainId}.${filterCode}`] ?? 'idle'

/**
 * selectFilterRateLimitRetryAfter — retry-after seconds for a
 * `<domainId>.<filterCode>` key paired with `'rate_limited'` status.
 * Returns `undefined` when not in a rate-limited state. Bug #3 (2026-05-14).
 */
export const selectFilterRateLimitRetryAfter =
  (domainId: string, filterCode: string) =>
  (state: RootState): number | undefined =>
    state.dashboardCreation.domainFilterRateLimitRetryAfter[
      `${domainId}.${filterCode}`
    ]

/**
 * selectSelectedFilterValues — the user's chosen value(s) for a
 * `<domainId>.<filterCode>` key. The single source of truth for reading user-
 * picked filter values out of `domainFilterValues` — earlier code paths used a
 * `selectFilterValue` alias that was an exact duplicate of this; consolidated
 * 2026-04-30 (PR #331 Claude review).
 */
export const selectSelectedFilterValues =
  (domainId: string, filterCode: string) =>
  (state: RootState): string | string[] | undefined =>
    state.dashboardCreation.domainFilterValues[`${domainId}.${filterCode}`]

export const selectHarvestContentState = (state: RootState): ModalContentState =>
  state.dashboardCreation.harvestContentState

export const selectHarvestCamps = (state: RootState): HarvestCamp[] =>
  state.dashboardCreation.harvestCamps

export const selectDashboardName = (state: RootState): string =>
  state.dashboardCreation.dashboardName

export const selectNameManuallyEdited = (state: RootState): boolean =>
  state.dashboardCreation.nameManuallyEdited

export const selectProposedPanelsStatus = (
  state: RootState
): 'idle' | 'loading' | 'ready' | 'fsm_conflict' | 'error' =>
  state.dashboardCreation.proposedPanelsStatus

export const selectProposedPanels = (state: RootState): ProposedPanelV1[] =>
  state.dashboardCreation.proposedPanels

export const selectConfirmedPanelIds = (state: RootState): string[] =>
  state.dashboardCreation.confirmedPanelIds

export const selectHarvestLiveViewUrl = (state: RootState): string | null =>
  state.dashboardCreation.harvestLiveViewUrl

export const selectHarvestSessionId = (state: RootState): string | null =>
  state.dashboardCreation.harvestSessionId

export const selectHarvestFailure = (
  state: RootState
): { code: string; partialSuccess: boolean } | null =>
  state.dashboardCreation.harvestFailure

export const selectHarvestSelectedClientId = (state: RootState): string | null =>
  state.dashboardCreation.harvestSelectedClientIdPreharvest

export const selectHarvestKeyExpiresAt = (state: RootState): string | null =>
  state.dashboardCreation.harvestKeyExpiresAt

export const selectHarvestCampName = (state: RootState): string | null =>
  state.dashboardCreation.harvestCampName

export const selectThinPreviewWarning = (
  state: RootState
): ThinPreviewWarning | null =>
  state.dashboardCreation.thinPreviewWarning

/**
 * selectAvailableCredentials — TDD §3d.1.E
 *
 * Empty array → render Harvest CTA (mockup 04).
 * Non-empty array → render radiogroup credential picker (mockup 05).
 */
export const selectAvailableCredentials = (state: RootState): CredentialOption[] =>
  state.dashboardCreation.availableCredentials

/**
 * selectCredentialPrefetchError — last error from the deterministic credential
 * prefetch path. Null when prefetch succeeded or has not run. Consumed by
 * CredentialPickerHarvestBlock to surface a visible error state.
 */
export const selectCredentialPrefetchError = (state: RootState): string | null =>
  state.dashboardCreation.credentialPrefetchError

export const selectBuildStatus = (
  state: RootState
): DashboardCreationState['buildStatus'] =>
  state.dashboardCreation.buildStatus

export const selectBuildErrorDomainId = (state: RootState): string | null =>
  state.dashboardCreation.buildErrorDomainId

/**
 * selectBuildErrorActualState — the BE-reported flow_state from the most recent
 * 409 INVALID_FSM_TRANSITION on the build endpoint. Null when not in
 * `buildStatus === 'fsm_conflict'` OR when the BE omitted actualState. Drives
 * step-specific copy in BuildErrorWell (Bug #4, 2026-05-14).
 */
export const selectBuildErrorActualState = (state: RootState): string | null =>
  state.dashboardCreation.buildErrorActualState
